/**
 * TripMileageForm — Kilometergeld-Erfassung einer Dienstreise (RK-7).
 *
 * Optionaler Block: km (Gesamt Hin+Rück, §6.1), Fahrzeugtyp (Pkw/Motorrad,
 * bestimmt den Satz §3.1) und eine Routen-Notiz (Eigenbeleg-Angabe,
 * §8.2 Recht-Recherche — `travelValidation.validateMileage` erzwingt das an
 * der Schreibgrenze). `null` bedeutet „keine eigene Fahrt" und ist der
 * Default.
 *
 * RK-17 (architecture-reisekosten.md §14.4, reine UI-Klarheit — keine Validierungs-
 * Änderung): km-Label „Gesamtkilometer (Hin- und Rückfahrt)" (`mileage.km`) +
 * Kurzhinweis `mileage.kmHint`.
 *
 * RK-21 (architecture-reisekosten.md §14.3/§14.4/§14.5, E15/E16/E20/V22): gate-gated
 * (`routeLookupEnabled`-Prop, Default `false` fail-closed — Verdrahtung via
 * `canUseRouteLookup(uid, tier)` in `TravelExpensesPage`) Start-/Ziel-Adressfelder +
 * „Distanz ermitteln"-Button + Vorschlags-Anzeige + „Übernehmen"-Button (`onRouteLookup`-Prop,
 * injizierbar, Muster E9 — Testbarkeit ohne echten Callable). Die Routen-Notiz-Kennzeichnung
 * wird dadurch KONDITIONAL (drei Zustände, {@link resolveRouteNoteState}): (a) keine/eine
 * Adresse ⇒ Pflicht, (b) beide Adressen gesetzt + kein Umweg ⇒ optional (E16), (c) Umweg
 * (`kmSource==='route_adjusted'` UND `km > suggestedKm`) ⇒ wieder Pflicht (A1/rev.5).
 *
 * **V22 HART (❌, §14.5):** ein erfolgreicher Distanz-Lookup ändert `value.km` NIEMALS
 * automatisch — der Vorschlag wird nur angezeigt (`suggestion`-State), die Übernahme passiert
 * ausschließlich über den expliziten Klick auf `handleAcceptSuggestion`. Bei Route lookupEnabled
 * `=== false` (Bestandsverhalten, Flag/Beta zu) rendert dieser Block gar nicht — byte-identisch
 * zum Stand vor RK-21 (Null-Delta, §14.6).
 *
 * **A-9 (Jurist-Wiedervorlage `plan-feedback-jurist-route-ga-wiedervorlage.md` § 3.2/§ 4.2,
 * 🔴 BLOCK, Nachfrist 2026-08-12) — ZWEI GETRENNTE HINWEISE, bewusst NICHT zusammengelegt:**
 *
 * 1. `mileage.routeLookupNotice` (Art. 13 Abs. 1 DSGVO, Übermittlungshinweis): steht DAUERHAFT
 *    im Routen-Block, oberhalb der Adressfelder, sobald `routeLookupEnabled` — insbesondere
 *    BEVOR der Nutzer eine Adresse eintippt und unabhängig davon, ob ein Lookup gelingt.
 *    Art. 13 Abs. 1 verlangt die Information „zum Zeitpunkt der Erhebung"; ein an `suggestion`
 *    gebundener Hinweis kann diesen Zeitpunkt strukturell nie erreichen.
 * 2. `mileage.attribution` (ODbL Ziff. 4.3 / ORS ToS, Lizenz-Attribution): bleibt UNVERÄNDERT
 *    ergebnisabhängig im `aria-live="polite"`-Bereich — die Attributionspflicht knüpft an die
 *    Anzeige eines aus OSM-Daten abgeleiteten „Produced Work" (= das km-Ergebnis) an; ohne
 *    Ergebnis gibt es nichts zu attribuieren.
 *
 * ⚠️ Die frühere O-16-Empfehlung (`jurist-ors-attribution-o16.md` (d)), beide zu EINER Einheit
 * zusammenzufassen, hat der Jurist ausdrücklich zurückgenommen: die beiden Hinweise haben
 * GEGENLÄUFIGE Zeitpunkte (ODbL frühestens mit dem Ergebnis, Art. 13 spätestens mit der
 * Erhebung) und passen deshalb nicht in ein Element. Die dadurch entstehende Redundanz beim
 * Anbieternamen ist gewollt und ist KEIN Anlass, die beiden Elemente erneut zu vereinigen.
 * Ebenfalls ausdrücklich NICHT vorgesehen: ein Consent-Häkchen oder ein blockierendes Gate —
 * für die Adressen Dritter könnte der Klickende gar keine wirksame Einwilligung erteilen.
 *
 * ⚠️ A11Y-FIX (RK-17-Regression, Tester-Fund): das Pflicht-Sternchen ist ein
 * `aria-hidden`-`<span>` AUSSERHALB des `<label>`-Elements (Geschwister-Element in
 * einem Flex-Wrapper), NICHT als Kind-Text im `<label>`. `<label htmlFor>` bindet den
 * Accessible Name des Inputs strikt an seinen eigenen Text-Inhalt — ein Sternchen
 * INNERHALB des Labels hätte den Accessible Name auf „mileage.routeNote *" verändert
 * und `getByLabelText('mileage.routeNote')`-Bestandstests (RK-7) brechen lassen. Die
 * Pflicht-Info selbst trägt ausschließlich `aria-required` am Input — dieser Fix bleibt in
 * RK-21 unverändert erhalten (das Sternchen wird nur noch KONDITIONAL gerendert).
 *
 * @module components/travel/TripMileageForm
 */

import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { IconCar, IconMapPin } from '../../icons';
import type { TravelMileage } from '../../utils/travel/travelCalcTypes';
import { buildRoundTripKmSuggestion } from '../../utils/travel/travelRouteSuggestion';
import { TravelRouteLookupError, type TravelRouteLookupResult } from '../../api/travelRouteApi';
import logger from '../../utils/logger';

interface TripMileageFormProps {
  value: TravelMileage | null;
  onChange: (value: TravelMileage | null) => void;
  disabled?: boolean;
  /**
   * RK-21 (§14.3 E20): schaltet den Start-/Ziel-Adress-Block frei. Default `false`
   * (fail-closed) — OHNE explizit `true` verhält sich diese Komponente byte-identisch zum
   * Stand vor RK-21 (kein Adress-Block, kein `travelRouteApi`-Aufruf-Pfad erreichbar).
   */
  routeLookupEnabled?: boolean;
  /**
   * RK-21 (§14.4): injizierter Lookup-Aufruf (Testbarkeit, Muster E9) — die Page verdrahtet
   * `onRouteLookup={lookupRouteDistance}` (`api/travelRouteApi.ts`). Wird NUR aufgerufen, wenn
   * `routeLookupEnabled` UND beide Adressfelder non-empty sind.
   */
  onRouteLookup?: (originAddress: string, destinationAddress: string) => Promise<TravelRouteLookupResult>;
}

const inputClass =
  'w-full bg-surface border border-hairline rounded-lg px-3 py-2 text-sm text-primary ' +
  'placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed';
const labelClass = 'block text-xs font-medium text-muted mb-1';

const DEFAULT_MILEAGE: TravelMileage = { km: 0, vehicle: 'car', routeNote: '' };

/** Deutsche Km-Anzeige (1 Dezimale, kompatibel zur `buildRoundTripKmSuggestion`-Rundung). */
const KM_FORMATTER = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

/**
 * RK-21 O-16 (Jurist-Freigabe `jurist-ors-attribution-o16.md`, ADVISORY, kein VETO): Ziel des
 * ODbL-Attributionslinks — feste Fundstelle, keine interne Route.
 */
const OSM_COPYRIGHT_URL = 'https://www.openstreetmap.org/copyright';

/**
 * RK-21 O-16: interner Pfad der Datenschutzerklärung. `/datenschutz` existiert als Route in
 * `App.tsx` (leitet client-seitig auf die Marketing-Site weiter) — kein neuer Pfad.
 */
const PRIVACY_POLICY_PATH = '/datenschutz';

/** Lokal gehaltenes Ergebnis eines erfolgreichen Lookups — noch NICHT übernommen (V22). */
interface RouteSuggestionState {
  km: number;
  originLabel: string;
  destinationLabel: string;
}

/** Lokal gehaltener Lookup-Fehler (Anzeige über `mileage.lookupErrors.<code>`). */
interface RouteLookupErrorState {
  code: TravelRouteLookupError['code'];
}

/**
 * Ermittelt den Pflicht-Zustand der Routen-Notiz (E16/A1, architecture-reisekosten.md
 * §14.3/§14.4). Spiegelt bewusst die Bedingung aus `travelValidation.validateMileage` (RK-19) —
 * OHNE gemeinsamen Import, damit dieses RK-21-Datei-Delta `travelValidation.ts` (außerhalb des
 * geplanten Datei-Deltas, §14.2) nicht anfasst. Bei einer künftigen Änderung der E16/A1-Regel
 * dort MUSS diese Funktion mitgezogen werden.
 */
function resolveRouteNoteState(mileage: TravelMileage): {
  required: boolean;
  hintKey: 'mileage.routeNoteRequiredHint' | 'mileage.routeNoteOptionalHint' | 'mileage.routeNoteDetourHint';
} {
  const originSet = (mileage.originAddress ?? '').trim().length > 0;
  const destinationSet = (mileage.destinationAddress ?? '').trim().length > 0;
  const bothAddressesSet = originSet && destinationSet;

  if (!bothAddressesSet) {
    return { required: true, hintKey: 'mileage.routeNoteRequiredHint' };
  }

  const isDetour =
    mileage.kmSource === 'route_adjusted' &&
    mileage.suggestedKm !== undefined &&
    mileage.km > mileage.suggestedKm;
  if (isDetour) {
    return { required: true, hintKey: 'mileage.routeNoteDetourHint' };
  }

  return { required: false, hintKey: 'mileage.routeNoteOptionalHint' };
}

/**
 * Formular für das (optionale) Kilometergeld einer Reise.
 */
const TripMileageForm: React.FC<TripMileageFormProps> = ({
  value,
  onChange,
  disabled = false,
  routeLookupEnabled = false,
  onRouteLookup,
}) => {
  const { t } = useTranslation('travel');
  const enabled = value !== null;

  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<RouteLookupErrorState | null>(null);
  const [suggestion, setSuggestion] = useState<RouteSuggestionState | null>(null);

  const set = <K extends keyof TravelMileage>(key: K, fieldValue: TravelMileage[K]): void => {
    if (value === null) return;
    onChange({ ...value, [key]: fieldValue });
  };

  /** km-Änderung: einmal übernommener Vorschlag (`route_suggested`) wird bei manueller
   *  Nach-Änderung zu `route_adjusted` (E15/E16) — jede andere Herkunft bleibt unverändert. */
  const handleKmChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (value === null) return;
    const nextKmSource = value.kmSource === 'route_suggested' ? 'route_adjusted' : value.kmSource;
    onChange({ ...value, km: e.target.valueAsNumber, kmSource: nextKmSource });
  };

  /** Adress-Änderung invalidiert einen ggf. bereits ermittelten, aber noch nicht übernommenen
   *  Vorschlag — sonst könnte „Übernehmen" nach einer Adress-Korrektur eine für die ALTE
   *  Adresse ermittelte Distanz auf die NEUE Adresse anwenden (Datenintegrität). */
  const setAddress = (key: 'originAddress' | 'destinationAddress', fieldValue: string): void => {
    setSuggestion(null);
    setLookupError(null);
    set(key, fieldValue);
  };

  const originAddress = value?.originAddress ?? '';
  const destinationAddress = value?.destinationAddress ?? '';
  const canLookup = !disabled && !lookupLoading && originAddress.trim().length > 0 && destinationAddress.trim().length > 0;

  const handleLookup = async (): Promise<void> => {
    if (value === null || !onRouteLookup) return;
    const origin = originAddress.trim();
    const destination = destinationAddress.trim();
    if (!origin || !destination) return;

    setLookupLoading(true);
    setLookupError(null);
    setSuggestion(null);
    try {
      const result = await onRouteLookup(origin, destination);
      const roundTripKm = buildRoundTripKmSuggestion(result.oneWayMeters);
      if (roundTripKm === null) {
        // Fail-closed (E15): eine degenerierte Distanz (z.B. auf 0 gerundet) ist KEIN
        // anzeigbarer Vorschlag — dieselbe neutrale Meldung wie ein Upstream-Fehler.
        setLookupError({ code: 'upstreamError' });
        return;
      }
      setSuggestion({ km: roundTripKm, originLabel: result.originLabel, destinationLabel: result.destinationLabel });
    } catch (err) {
      if (err instanceof TravelRouteLookupError) {
        setLookupError({ code: err.code });
      } else {
        logger.error('TripMileageForm: unerwarteter Fehler beim Distanz-Lookup', err);
        setLookupError({ code: 'network' });
      }
    } finally {
      setLookupLoading(false);
    }
  };

  /** V22 HART: einzige Stelle, die `km` aus einem Lookup-Ergebnis setzt — NUR per explizitem Klick. */
  const handleAcceptSuggestion = (): void => {
    if (value === null || suggestion === null) return;
    onChange({
      ...value,
      km: suggestion.km,
      suggestedKm: suggestion.km,
      kmSource: 'route_suggested',
    });
    setSuggestion(null);
    setLookupError(null);
  };

  const routeNoteState = enabled && value !== null ? resolveRouteNoteState(value) : null;

  return (
    <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] border border-hairline rounded-xl p-4 md:p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-primary flex items-center gap-2">
          <IconCar className="text-accent-ink" />
          {t('mileage.title')}
        </h2>
        <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-muted">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onChange(e.target.checked ? { ...DEFAULT_MILEAGE } : null)}
            disabled={disabled}
          />
          {t('mileage.enable')}
        </label>
      </div>

      {enabled && value !== null && routeNoteState !== null && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass} htmlFor="trip-mileage-km">
                {t('mileage.km')}
              </label>
              <input
                id="trip-mileage-km"
                type="number"
                min={0}
                step={0.1}
                className={inputClass}
                value={Number.isFinite(value.km) ? value.km : ''}
                onChange={handleKmChange}
                disabled={disabled}
              />
              <p className="text-xs text-faint mt-1">{t('mileage.kmHint')}</p>
            </div>

            <div>
              <label className={labelClass} htmlFor="trip-mileage-vehicle">
                {t('mileage.vehicle')}
              </label>
              <select
                id="trip-mileage-vehicle"
                className={inputClass}
                value={value.vehicle}
                onChange={(e) => set('vehicle', e.target.value as TravelMileage['vehicle'])}
                disabled={disabled}
              >
                <option value="car">{t('mileage.vehicleCar')}</option>
                <option value="motorcycle">{t('mileage.vehicleMotorcycle')}</option>
              </select>
            </div>

            <div className="md:col-span-1">
              <div className="flex items-center gap-1 mb-1">
                <label className={`${labelClass} mb-0`} htmlFor="trip-mileage-routeNote">
                  {t('mileage.routeNote')}
                </label>
                {routeNoteState.required && (
                  <span aria-hidden="true" className="text-[var(--tone-danger-fg)]">
                    *
                  </span>
                )}
              </div>
              <input
                id="trip-mileage-routeNote"
                type="text"
                className={inputClass}
                value={value.routeNote}
                onChange={(e) => set('routeNote', e.target.value)}
                placeholder={t('mileage.routeNotePlaceholder')}
                disabled={disabled}
                aria-required={routeNoteState.required}
              />
              <p className="text-xs text-faint mt-1">{t(routeNoteState.hintKey)}</p>
            </div>
          </div>

          {routeLookupEnabled && (
            <div className="pt-3 border-t border-hairline space-y-3">
              <h3 className="text-xs font-semibold text-secondary flex items-center gap-2">
                <IconMapPin size={14} className="text-accent-ink" />
                {t('mileage.routeLookupTitle')}
              </h3>

              {/* A-9 (Jurist-Wiedervorlage § 4.2): Art.-13-Abs.-1-Übermittlungshinweis — DAUERHAFT,
                  sobald der Routen-Block sichtbar ist, und OBERHALB der Adressfelder. NICHT an
                  `suggestion` binden und NICHT wieder mit `mileage.attribution` zusammenlegen (s.
                  Datei-Kopf: gegenläufige Zeitpunkte). */}
              <p className="text-[11px] text-faint">
                <Trans
                  t={t}
                  i18nKey="mileage.routeLookupNotice"
                  components={{
                    privacyLink: (
                      <a
                        href={PRIVACY_POLICY_PATH}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-secondary"
                      />
                    ),
                  }}
                />
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="trip-mileage-origin">
                    {t('mileage.originAddress')}
                  </label>
                  <input
                    id="trip-mileage-origin"
                    type="text"
                    className={inputClass}
                    value={originAddress}
                    onChange={(e) => setAddress('originAddress', e.target.value)}
                    placeholder={t('mileage.originAddressPlaceholder')}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="trip-mileage-destination">
                    {t('mileage.destinationAddress')}
                  </label>
                  <input
                    id="trip-mileage-destination"
                    type="text"
                    className={inputClass}
                    value={destinationAddress}
                    onChange={(e) => setAddress('destinationAddress', e.target.value)}
                    placeholder={t('mileage.destinationAddressPlaceholder')}
                    disabled={disabled}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleLookup}
                disabled={!canLookup}
                className="px-3 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-[#ffffff] rounded-lg transition-colors"
              >
                {lookupLoading ? t('mileage.lookupButtonLoading') : t('mileage.lookupButton')}
              </button>

              <div aria-live="polite" className="space-y-2">
                {lookupError && (
                  <p className="text-xs text-[var(--tone-danger-fg)]">{t(`mileage.lookupErrors.${lookupError.code}`)}</p>
                )}
                {suggestion && (
                  <div className="bg-[rgb(var(--surface-base-rgb)/0.6)] border border-hairline rounded-lg p-3 space-y-2">
                    <p className="text-sm text-primary font-medium">
                      {t('mileage.suggestion', { km: KM_FORMATTER.format(suggestion.km) })}
                    </p>
                    <p className="text-xs text-muted">
                      {t('mileage.suggestionOneWayHint', {
                        originLabel: suggestion.originLabel,
                        destinationLabel: suggestion.destinationLabel,
                      })}
                    </p>
                    <button
                      type="button"
                      onClick={handleAcceptSuggestion}
                      disabled={disabled}
                      className="px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-[#ffffff] rounded-lg transition-colors"
                    >
                      {t('mileage.acceptSuggestion')}
                    </button>
                    {/* O-16: kombinierte ORS/OSM-Attribution + DSGVO-Übermittlungshinweis — NUR
                        hier (im Vorschlags-Zweig), nicht dauerhaft im Formular (s. Datei-Kopf). */}
                    <p className="text-[11px] text-faint">
                      <Trans
                        t={t}
                        i18nKey="mileage.attribution"
                        components={{
                          osmLink: (
                            <a
                              href={OSM_COPYRIGHT_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:text-secondary"
                            />
                          ),
                          privacyLink: (
                            <a
                              href={PRIVACY_POLICY_PATH}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:text-secondary"
                            />
                          ),
                        }}
                      />
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TripMileageForm;
